"use client";

/**
 * components/video/TestimonialPlayer.tsx — website copy (2026-09-25, Vela,
 * Wall of Love build) of pf-platform components/testimonials/TestimonialPlayer.tsx
 * at origin/staging. Copied, not shared, exactly as that file was itself copied
 * from this repo's Pro Member carousel player (CarouselVideoPlayer.tsx), which
 * is carousel-coupled (one element moved between cards, a fixed pull zone, no
 * trim) and cannot play a Testimonials-library video on its own. Behaviour is
 * byte-for-byte the platform copy; only the import paths and the token aliases
 * in TestimonialPlayerStyles differ. Never the locked lesson player
 * (SOP-PLATFORM-04): nothing here imports or edits it.
 *
 * Original header follows.
 */


/**
 * TestimonialPlayer — OUR video player for testimonials (Round 4, Lars
 * 2026-09-24: "our own video player everywhere, never Senja's or Bunny's look").
 *
 * COPIED, not shared: the source is the website's Pro Member carousel player,
 * pianofantasy-web app/pro-member/_client/CarouselVideoPlayer.tsx (+ its
 * `.pm12-player` / `lps-*` CSS in app/pro-member/_v12/pm12-carousel.css), which
 * is itself copied from this repo's lesson player. The locked lesson player
 * files (SOP-PLATFORM-04: LessonPlayerShell.tsx, HlsPlayer.tsx, their CSS) are
 * only READ, never imported or edited.
 *
 * Class names: every `tplayer-*` class here is the lesson player's `lps-*`
 * class of the same suffix (tplayer-control-bar = lps-control-bar, …). They are
 * renamed because pf-platform's globals.css styles `lps-*` unscoped, so reusing
 * the names would pull the lesson player's own rules into this player. The one
 * exception is the buffering mark, `.lps-buffering-overlay .lv-equaliser`,
 * which is reused as-is from globals.css (it is not part of the locked block).
 *
 * What was taken, what was left behind:
 *   kept   — play/pause · desktop volume + slider · time · scrubber (tap and
 *            drag, 24px touch band, arrow keys) · captions button + ⋯ menu
 *            (Quality: Auto + renditions; Captions: the tracks given) ·
 *            fullscreen incl. iPhone's webkitEnterFullscreen · 1.5s idle-hide ·
 *            tap-anywhere play/pause · the progress-based buffering mark ·
 *            hls.js on desktop/Android, native HLS first on iOS, 480p start.
 *   left   — the carousel machinery (one element moved between cards, spare
 *            element unlock, standby priming, stream warming, media-quiet).
 *   added  — `source.kind === "file"`: a LOCAL blob/object URL (non-HLS), so a
 *            recording or picked file can be watched back instantly before any
 *            upload (Part B). The box takes the video's own shape (portrait
 *            phone video stays portrait), letterboxed on the player's dark.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type HlsClass from "hls.js";
import { TestimonialPlayerStyles } from "./TestimonialPlayerStyles";
import { clipWindow } from "@/lib/testimonial-trim";
import PfPlayButton from "./PfPlayButton";

type Hls = HlsClass;
type HlsCtor = typeof HlsClass;

export type TestimonialPlayerSource =
  | { kind: "bunny"; videoId: string; cdnHostname: string }
  | { kind: "file"; url: string };

export interface TestimonialPlayerTrack {
  locale: string;
  label: string;
  url: string;
}

interface Props {
  source: TestimonialPlayerSource;
  /** Accessible name of the video, e.g. "Video testimonial from Iolo". */
  title: string;
  /** Poster image; for Bunny sources the library thumbnail is used when omitted. */
  poster?: string | null;
  /** Caption tracks (WebVTT). Testimonials have none today; the CC button hides then. */
  tracks?: TestimonialPlayerTrack[];
  /** Tallest the player may be (CSS length). Default 70vh. */
  maxHeight?: string;
  /**
   * Length in seconds when the file itself does not say (Part B): a browser
   * recording in webm carries no duration header, so the element reports
   * Infinity. The recorder knows how long it recorded and passes that here.
   */
  durationHint?: number;
  /**
   * Round 6 (plan item 7): play only [trimIn, trimOut] (seconds; null = the
   * video's own edge). The file is never cut — the timeline, the time label
   * and every seek stay inside the window, and play starts at trimIn.
   */
  clip?: { trimIn?: number | null; trimOut?: number | null } | null;
  /**
   * Round 6 pass 2: hands the player's <video> to the trim editor beside it
   * ("Add marker" reads its time, a marker tap seeks it). Optional; no other
   * behaviour changes.
   */
  onVideoElement?: (v: HTMLVideoElement | null) => void;
  /** Round 6e: the big play disc's accessible name (default "Play: <title>"). */
  playLabel?: string;
  /** Round 6e: extra content over the video (the thank-you screen's download button). */
  overlay?: ReactNode;
  /**
   * Wall of Love R4: "kit" swaps the purple play disc for the brand kit's
   * canonical §14.2 `.pf-play-btn` (components/video/PfPlayButton). Default
   * "disc" keeps the player exactly as before.
   */
  bigPlay?: "disc" | "kit";
}

const IS_IOS =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

/** Carousel player values (round 14): idle-hide and the buffering mark's delay. */
const IDLE_HIDE_MS = 1500;
const SPINNER_STILL_MS = 500;
const STALL_NUDGE_MS = 4000;

/**
 * The 4:3 standard (Lars 2026-09-25): every player is a 4:3 frame. A 4:3
 * video fills it; any other shape (uploads, portrait phone files, older 16:9
 * imports) is shown WHOLE (contain) on a blurred copy of itself — the Wall of
 * Love's values: cover, blur 20px, scale 1.15, opacity 0.6. Nothing is ever
 * cropped or re-encoded; this is display only.
 */
const FRAME_RATIO = 4 / 3;
/** A video within this of 4:3 fills the frame: no blurred copy behind it. */
const RATIO_TOLERANCE = 0.01;
/** The blurred copy painted from the video itself (no poster): a small canvas, repainted this often while playing. */
const BLUR_CANVAS_W = 64;
const BLUR_REPAINT_MS = 250;

const hlsUrl = (s: { videoId: string; cdnHostname: string }) =>
  `https://${s.cdnHostname}/${encodeURIComponent(s.videoId)}/playlist.m3u8`;
const thumbUrl = (s: { videoId: string; cdnHostname: string }) =>
  `https://${s.cdnHostname}/${encodeURIComponent(s.videoId)}/thumbnail.jpg`;

interface Cue {
  start: number;
  end: number;
  text: string;
}
function vttTime(s: string): number {
  let sec = 0;
  for (const part of s.trim().split(":")) sec = sec * 60 + parseFloat(part.replace(",", "."));
  return sec;
}
function parseVtt(text: string): Cue[] {
  const cues: Cue[] = [];
  for (const block of text.replace(/^\uFEFF/, "").replace(/\r/g, "").split(/\n{2,}/)) {
    const lines = block.split("\n");
    const i = lines.findIndex((l) => l.includes("-->"));
    if (i === -1) continue;
    const [a, b] = lines[i].split("-->");
    const start = vttTime(a);
    const end = vttTime(b.trim().split(/\s+/)[0]);
    const body = lines.slice(i + 1).join("\n").replace(/<[^>]+>/g, "").trim();
    if (Number.isFinite(start) && Number.isFinite(end) && body) cues.push({ start, end, text: body });
  }
  return cues;
}

/** LessonPlayerShell `fmtTime`. */
function fmtTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = String(Math.floor(sec % 60)).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

type FsDoc = Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
type FsEl = HTMLElement & { webkitRequestFullscreen?: () => void };
type IosVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };
function fullscreenElement(): Element | null {
  if (typeof document === "undefined") return null;
  const d = document as FsDoc;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

/* ── Icons — as in the carousel player (lucide paths the lesson player renders). ── */
function Lucide({ size, children }: { size: number; children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}
const PlayIcon = ({ size = 26 }: { size?: number }) => (
  <Lucide size={size}>
    <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
  </Lucide>
);
const PauseIcon = ({ size = 26 }: { size?: number }) => (
  <Lucide size={size}>
    <rect x="14" y="3" width="5" height="18" rx="1" />
    <rect x="5" y="3" width="5" height="18" rx="1" />
  </Lucide>
);
const VolumeIcon = ({ size = 24 }: { size?: number }) => (
  <Lucide size={size}>
    <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
    <path d="M16 9a5 5 0 0 1 0 6" />
    <path d="M19.364 18.364a9 9 0 0 0 0-12.728" />
  </Lucide>
);
const VolumeXIcon = ({ size = 24 }: { size?: number }) => (
  <Lucide size={size}>
    <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
    <line x1="22" x2="16" y1="9" y2="15" />
    <line x1="16" x2="22" y1="9" y2="15" />
  </Lucide>
);
const MoreIcon = ({ size = 24 }: { size?: number }) => (
  <Lucide size={size}>
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
    <circle cx="5" cy="12" r="1" />
  </Lucide>
);
const CheckIcon = ({ size = 14 }: { size?: number }) => (
  <Lucide size={size}>
    <path d="M20 6 9 17l-5-5" />
  </Lucide>
);
const ChevronLeftIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14.5 4.5 C11.87 6.35 9.52 8.49 7.5 11.0" strokeWidth={1.5} />
    <path d="M7.5 11.0 C9.54 13.48 11.85 15.67 14.5 17.5" strokeWidth={1.5} />
  </svg>
);
function CCIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="1.5" y="4.5" width="21" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M10.2 10.1c-.5-.55-1.2-.85-2-.85-1.55 0-2.7 1.2-2.7 2.75s1.15 2.75 2.7 2.75c.8 0 1.5-.3 2-.85" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M18.5 10.1c-.5-.55-1.2-.85-2-.85-1.55 0-2.7 1.2-2.7 2.75s1.15 2.75 2.7 2.75c.8 0 1.5-.3 2-.85" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function ExpandIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 3H3v6M15 3h6v6M15 21h6v-6M9 21H3v-6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function CollapseIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 9h6V3M15 3v6h6M21 15h-6v6M9 21v-6H3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function TestimonialPlayer({ source, title, poster, tracks = [], maxHeight = "70vh", durationHint, clip, onVideoElement, playLabel, overlay, bigPlay = "disc" }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (!onVideoElement) return;
    onVideoElement(videoRef.current);
    return () => onVideoElement(null);
  }, [onVideoElement]);
  const hlsRef = useRef<Hls | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const scrubberRef = useRef<HTMLDivElement>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const timeCurRef = useRef<HTMLSpanElement>(null);
  const timeDurRef = useRef<HTMLSpanElement>(null);

  /** The video's own shape (width / height), null until its metadata is read. */
  const [ratio, setRatio] = useState<number | null>(null);
  const blurCanvasRef = useRef<HTMLCanvasElement>(null);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [renditions, setRenditions] = useState<{ index: number; height: number }[]>([]);
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);
  const [activeHeight, setActiveHeight] = useState<number | null>(null);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [locale, setLocale] = useState<string | null>(null);
  const [cues, setCues] = useState<Cue[]>([]);
  const [cueText, setCueText] = useState<string | null>(null);
  const [menu, setMenu] = useState<null | "root" | "captions" | "quality">(null);
  const [menuPos, setMenuPos] = useState<{ bottom: number; maxHeight: number } | null>(null);
  const [isFs, setIsFs] = useState(false);
  const [idleHidden, setIdleHidden] = useState(false);
  const [scrub, setScrub] = useState<number | null>(null);
  const [kbdFocusIn, setKbdFocusIn] = useState(false);
  const [failed, setFailed] = useState(false);

  const timeRef = useRef(0);
  const durationRef = useRef(0);
  const scrubRef = useRef<number | null>(null);
  const cuesRef = useRef<Cue[]>([]);
  const captionsOnRef = useRef(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuFocusRef = useRef<null | "first" | "captions" | "quality" | "more">(null);
  /** Round 6: the trim window, read by the painter and the media handlers without re-binding them. */
  const clipRef = useRef(clip);
  clipRef.current = clip;
  const windowOf = useCallback(() => clipWindow(clipRef.current, durationRef.current), []);

  const posterSrc = poster ?? (source.kind === "bunny" ? thumbUrl(source) : undefined);
  const sourceKey = source.kind === "bunny" ? `b:${source.cdnHostname}/${source.videoId}` : `f:${source.url}`;

  /* ── Buffering mark (carousel D1): shown only when playback should move and
   *    currentTime has not advanced for SPINNER_STILL_MS. DOM-toggled. ── */
  const spinOn = useRef(false);
  const setBuffering = useCallback((on: boolean) => {
    if (spinOn.current === on) return;
    spinOn.current = on;
    if (spinnerRef.current) spinnerRef.current.style.display = on ? "" : "none";
  }, []);

  /* ── Per-frame UI painter: scrubber, time, aria — DOM writes, no render. ── */
  const paint = useCallback(() => {
    // Times shown are relative to the trim window's start (0 when untrimmed).
    const w = windowOf();
    const t = Math.max(0, (scrubRef.current ?? timeRef.current) - w.start);
    const d = Math.max(0, (w.end ?? durationRef.current) - w.start);
    const pct = d > 0 ? Math.min(100, (t / d) * 100) : 0;
    if (fillRef.current) fillRef.current.style.width = `${pct}%`;
    if (thumbRef.current) thumbRef.current.style.left = `${pct}%`;
    if (timeCurRef.current) timeCurRef.current.textContent = fmtTime(t);
    if (timeDurRef.current) timeDurRef.current.textContent = ` / ${fmtTime(d)}`;
    const sl = scrubberRef.current;
    if (sl) {
      sl.setAttribute("aria-valuenow", String(Math.floor(t)));
      sl.setAttribute("aria-valuetext", `${fmtTime(t)} of ${fmtTime(d)}`);
    }
  }, [windowOf]);
  durationRef.current = duration;
  useLayoutEffect(() => {
    paint();
  });

  /* ── Source: Bunny HLS (hls.js / native on iOS) or a local file URL. ── */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let alive = true;
    setStarted(false);
    setPlaying(false);
    setFailed(false);
    setRenditions([]);
    setActiveHeight(null);
    timeRef.current = 0;
    setRatio(null);
    setDuration(source.kind === "file" && durationHint && durationHint > 0 ? durationHint : 0);

    if (source.kind === "file") {
      v.src = source.url;
      v.preload = "metadata";
    } else {
      const url = hlsUrl(source);
      v.preload = "metadata";
      if (IS_IOS && v.canPlayType("application/vnd.apple.mpegurl")) {
        v.src = url;
      } else {
        import("hls.js")
          .then((m) => {
            if (!alive) return;
            const Ctor = (m as unknown as { default: HlsCtor }).default;
            if (!Ctor.isSupported()) {
              if (v.canPlayType("application/vnd.apple.mpegurl")) v.src = url;
              else setFailed(true);
              return;
            }
            // HlsPlayer.tsx config; the carousel's 480p start (Auto stays on).
            const hls = new Ctor({
              startPosition: -1,
              maxBufferLength: 30,
              maxMaxBufferLength: 60,
              abrEwmaDefaultEstimate: 5_000_000,
              abrBandWidthUpFactor: 0.9,
            });
            hlsRef.current = hls;
            hls.loadSource(url);
            hls.attachMedia(v);
            hls.on(Ctor.Events.MANIFEST_PARSED, () => {
              const lv = hls.levels ?? [];
              if (lv.length > 0) {
                let startIdx = 0;
                for (let i = 1; i < lv.length; i++) {
                  if (Math.abs((lv[i].height ?? 0) - 480) < Math.abs((lv[startIdx].height ?? 0) - 480)) startIdx = i;
                }
                hls.startLevel = startIdx;
              }
              setRenditions(lv.map((l, index) => ({ index, height: l.height ?? 0 })).sort((a, b) => b.height - a.height));
            });
            hls.on(Ctor.Events.LEVEL_SWITCHED, (_e, data) => setActiveHeight(hls.levels?.[data.level]?.height ?? null));
            hls.on(Ctor.Events.ERROR, (_e, data) => {
              if (!data.fatal) return;
              if (data.type === Ctor.ErrorTypes.NETWORK_ERROR) hls.startLoad();
              else if (data.type === Ctor.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
              else setFailed(true);
            });
          })
          .catch(() => {
            if (alive) setFailed(true);
          });
      }
    }
    return () => {
      alive = false;
      hlsRef.current?.destroy();
      hlsRef.current = null;
      try {
        v.pause();
        v.removeAttribute("src");
        v.load();
      } catch {
        /* detached */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey]);

  /* ── Media events → player state ── */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let raf = 0;
    let lastCt = -1;
    let lastAdv = 0;
    const tick = () => {
      raf = 0;
      if (v.paused || v.ended) {
        setBuffering(false);
        return;
      }
      const ct = v.currentTime;
      if (stopAtClipEnd()) return;
      const now = performance.now();
      if (Math.abs(ct - lastCt) >= 0.001) {
        lastCt = ct;
        lastAdv = now;
        setBuffering(false);
      } else if (now - lastAdv >= SPINNER_STILL_MS) setBuffering(true);
      raf = requestAnimationFrame(tick);
    };
    // Round 6: at the trim's out-point playback stops exactly there (checked
    // every frame while playing, not only on the ~4 Hz timeupdate).
    function stopAtClipEnd(): boolean {
      const w = windowOf();
      if (w.end === null || w.end >= (durationRef.current || Infinity) - 0.01) return false;
      if (v!.currentTime < w.end - 0.02) return false;
      v!.pause();
      try {
        v!.currentTime = w.end;
      } catch {
        /* not seekable */
      }
      timeRef.current = w.end;
      paint();
      return true;
    }
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const clearStall = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = null;
    };
    const onPlay = () => {
      lastCt = v.currentTime;
      lastAdv = performance.now();
      if (!raf) raf = requestAnimationFrame(tick);
      setPlaying(true);
      setStarted(true);
    };
    const onPause = () => {
      setPlaying(false);
      setBuffering(false);
    };
    const onWaiting = () => {
      if (v.paused || stallTimer) return;
      const at = v.currentTime;
      stallTimer = setTimeout(() => {
        stallTimer = null;
        if (v.paused || v.currentTime !== at || v.readyState >= 3) return;
        const hls = hlsRef.current;
        if (hls) hls.startLoad(at);
        else {
          try {
            v.currentTime = at;
          } catch {
            /* not seekable */
          }
        }
      }, STALL_NUDGE_MS);
    };
    const onTime = () => {
      if (!v.paused) stopAtClipEnd();
      timeRef.current = v.currentTime;
      if (scrubRef.current === null) paint();
      const next = captionsOnRef.current
        ? cuesRef.current.find((c) => v.currentTime >= c.start && v.currentTime < c.end)?.text ?? null
        : null;
      setCueText((prev) => (prev === next ? prev : next));
    };
    const onDur = () => {
      if (isFinite(v.duration) && v.duration > 0) setDuration(v.duration);
      else if (durationHint && durationHint > 0) setDuration(durationHint);
    };
    const onMeta = () => {
      onDur();
      // Round 6: a trimmed video opens at its in-point.
      const w = windowOf();
      if (w.start > 0 && v.currentTime < w.start) {
        try {
          v.currentTime = w.start;
        } catch {
          /* not seekable yet */
        }
        timeRef.current = w.start;
      }
      if (v.videoWidth > 0 && v.videoHeight > 0) setRatio(v.videoWidth / v.videoHeight);
    };
    /** The video's shape can change mid-stream (an adaptive rendition, a turned phone file). */
    const onShape = () => {
      if (v.videoWidth > 0 && v.videoHeight > 0) setRatio(v.videoWidth / v.videoHeight);
    };
    const onVol = () => {
      setMuted(v.muted);
      setVolume(v.volume);
    };
    const onError = () => {
      if (source.kind === "file") setFailed(true);
    };
    const onBeginFs = () => setIsFs(true);
    const onEndFs = () => setIsFs(false);
    const ev: [string, EventListener][] = [
      ["play", onPlay],
      ["pause", onPause],
      ["ended", onPause],
      ["waiting", onWaiting],
      ["stalled", onWaiting],
      ["playing", clearStall],
      ["canplay", clearStall],
      ["timeupdate", onTime],
      ["durationchange", onDur],
      ["loadedmetadata", onMeta],
      ["resize", onShape],
      ["volumechange", onVol],
      ["error", onError],
      ["webkitbeginfullscreen", onBeginFs],
      ["webkitendfullscreen", onEndFs],
    ];
    for (const [n, f] of ev) v.addEventListener(n, f);
    return () => {
      for (const [n, f] of ev) v.removeEventListener(n, f);
      clearStall();
      if (raf) cancelAnimationFrame(raf);
      setBuffering(false);
    };
  }, [paint, setBuffering, source.kind, durationHint, windowOf]);

  /* ── Captions (only when tracks are given) ── */
  const activeTrack = tracks.find((t) => t.locale === locale) ?? tracks[0] ?? null;
  captionsOnRef.current = captionsOn;
  cuesRef.current = cues;
  useEffect(() => {
    if (!captionsOn || !activeTrack) {
      setCues([]);
      return;
    }
    let alive = true;
    fetch(activeTrack.url)
      .then((r) => (r.ok ? r.text() : ""))
      .then((t) => {
        if (alive) setCues(parseVtt(t));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [captionsOn, activeTrack?.url]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = timeRef.current;
    setCueText(captionsOn ? cues.find((c) => t >= c.start && t < c.end)?.text ?? null : null);
  }, [captionsOn, cues]);

  /* ── Fullscreen API state (desktop, Android) ── */
  useEffect(() => {
    const onChange = () => setIsFs(!!rootRef.current && fullscreenElement() === rootRef.current);
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  /* ── Idle hide (1.5s while playing; keyboard focus in the bar holds it) ── */
  const menuOpen = menu !== null;
  const idleActive = playing && scrub === null && !menuOpen && !kbdFocusIn;
  const idleActiveRef = useRef(idleActive);
  idleActiveRef.current = idleActive;
  const reveal = useCallback(() => {
    setIdleHidden(false);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = null;
    if (idleActiveRef.current) {
      idleTimer.current = setTimeout(() => {
        if (idleActiveRef.current) setIdleHidden(true);
      }, IDLE_HIDE_MS);
    }
  }, []);
  useEffect(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = null;
    if (!idleActive) setIdleHidden(false);
    else
      idleTimer.current = setTimeout(() => {
        if (idleActiveRef.current) setIdleHidden(true);
      }, IDLE_HIDE_MS);
  }, [idleActive]);
  useEffect(
    () => () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
    },
    []
  );
  const controlsHidden = idleHidden && idleActive;

  /* ── Control-bar height → caption lift (the carousel's --pm12-cap-lift) ── */
  useLayoutEffect(() => {
    const bar = barRef.current;
    const root = rootRef.current;
    if (!bar || !root) return;
    const apply = () => {
      const h = bar.offsetHeight;
      const sc = scrubberRef.current;
      const row = bar.querySelector<HTMLElement>(".tplayer-controls-row");
      let top = sc ? sc.offsetTop : 0;
      if (row && row.offsetTop < top) top = row.offsetTop;
      root.style.setProperty("--tplayer-cap-lift", `${Math.max(8, h - top + 8)}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(bar);
    return () => ro.disconnect();
  }, []);

  /* ── ⋯ menu placement: above the bar, shifted/capped to fit ── */
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!menuOpen || !root || !menuRef.current || !barRef.current) {
      setMenuPos(null);
      return;
    }
    const rootH = root.getBoundingClientRect().height;
    const barH = barRef.current.getBoundingClientRect().height;
    const menuH = menuRef.current.scrollHeight;
    const maxH = Math.max(0, rootH - 16);
    const bottom = Math.max(8, Math.min(barH + 8, rootH - Math.min(menuH, maxH) - 8));
    setMenuPos({ bottom, maxHeight: maxH });
  }, [menuOpen, menu, tracks.length, renditions.length, isFs]);
  useEffect(() => {
    const target = menuFocusRef.current;
    if (!target) return;
    if (target === "more") {
      menuFocusRef.current = null;
      moreBtnRef.current?.focus();
      return;
    }
    const pop = menuRef.current;
    if (!pop || !menuPos) return;
    menuFocusRef.current = null;
    const el =
      target === "first"
        ? pop.querySelector<HTMLElement>(".tplayer-settings-option, .tplayer-settings-nav-row:not(.tplayer-settings-nav-row--static)")
        : pop.querySelector<HTMLElement>(`[data-tplayer-row="${target}"]`);
    el?.focus();
  }, [menu, menuPos]);

  /* ── Actions ── */
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      setStarted(true);
      v.muted = false;
      // Round 6: play is always inside the trim window — from the in-point
      // when before it, or again from the in-point once the out-point was reached.
      const w = windowOf();
      if (v.currentTime < w.start - 0.05 || (w.end !== null && v.currentTime >= w.end - 0.05)) {
        try {
          v.currentTime = w.start;
        } catch {
          /* not seekable */
        }
        timeRef.current = w.start;
      }
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else v.pause();
  };
  const onTap = () => {
    if (menuOpen) {
      setMenu(null);
      return;
    }
    togglePlay();
    reveal();
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted && v.volume === 0) v.volume = 1;
  };
  const toggleFullscreen = () => {
    const v = videoRef.current as IosVideo | null;
    const root = rootRef.current;
    if (!v || !root) return;
    if (fullscreenElement()) {
      const d = document as FsDoc;
      (d.exitFullscreen ? d.exitFullscreen() : d.webkitExitFullscreen?.())?.catch?.(() => {});
      return;
    }
    if (IS_IOS && typeof v.webkitEnterFullscreen === "function") {
      try {
        v.webkitEnterFullscreen();
      } catch {
        /* not ready yet */
      }
      return;
    }
    const r = root as FsEl;
    if (r.requestFullscreen) r.requestFullscreen().catch(() => {});
    else r.webkitRequestFullscreen?.();
  };
  const applyQuality = (index: number, kbd = false) => {
    const hls = hlsRef.current;
    if (!hls) return;
    hls.currentLevel = index;
    setLockedHeight(index < 0 ? null : hls.levels?.[index]?.height ?? null);
    if (kbd) menuFocusRef.current = "more";
    setMenu(null);
  };

  /* ── Scrubbing: pointer-driven, so a TAP on the track seeks everywhere ── */
  const scrubState = useRef<{ id: number; x0: number; moved: boolean } | null>(null);
  const timeAt = (clientX: number) => {
    const el = scrubberRef.current;
    const full = duration || videoRef.current?.duration || 0;
    const w = clipWindow(clip, full);
    const d = (w.end ?? full) - w.start;
    if (!el || !(d > 0)) return w.start;
    const r = el.getBoundingClientRect();
    return w.start + Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * d;
  };
  const seek = (t: number) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.currentTime = t;
    } catch {
      /* not seekable yet */
    }
    timeRef.current = t;
    paint();
  };
  const onScrubDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const mouse = e.pointerType === "mouse";
    scrubState.current = { id: e.pointerId, x0: e.clientX, moved: mouse };
    if (mouse) {
      scrubRef.current = timeAt(e.clientX);
      setScrub(scrubRef.current);
      paint();
    }
  };
  const onScrubMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const s = scrubState.current;
    if (!s || s.id !== e.pointerId) return;
    if (!s.moved && Math.abs(e.clientX - s.x0) < 4) return;
    s.moved = true;
    scrubRef.current = timeAt(e.clientX);
    setScrub(scrubRef.current);
    paint();
  };
  const onScrubUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const s = scrubState.current;
    if (!s || s.id !== e.pointerId) return;
    scrubState.current = null;
    scrubRef.current = null;
    seek(timeAt(e.clientX));
    setScrub(null);
    reveal();
  };
  const onScrubCancel = () => {
    scrubState.current = null;
    scrubRef.current = null;
    setScrub(null);
    paint();
  };
  const onScrubKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const w = clipWindow(clip, duration || 0);
    const lo = w.start;
    const hi = w.end ?? (duration || 0);
    const time = timeRef.current;
    let t: number | null = null;
    if (e.key === "ArrowLeft") t = Math.max(lo, time - 5);
    else if (e.key === "ArrowRight") t = Math.min(hi, time + 5);
    else if (e.key === "Home") t = lo;
    else if (e.key === "End") t = hi;
    if (t === null) return;
    e.preventDefault();
    seek(t);
  };

  const shownWindow = clipWindow(clip, duration);
  const shownTime = Math.max(0, (scrub ?? timeRef.current) - shownWindow.start);
  const shownDuration = Math.max(0, (shownWindow.end ?? duration) - shownWindow.start);
  const pct = shownDuration > 0 ? Math.min(100, (shownTime / shownDuration) * 100) : 0;
  const hasCaptions = tracks.length > 0;
  const activeLabel = activeTrack?.label ?? null;

  // The 4:3 standard: a video that is not 4:3 sits on a blurred copy of
  // itself. With a poster that copy is the poster (as on the Wall of Love);
  // without one it is painted from the video on a small canvas — at the first
  // frame, on every seek and pause, and a few times a second while playing.
  const banded = ratio === null || Math.abs(ratio - FRAME_RATIO) > RATIO_TOLERANCE;
  const blurFromVideo = banded && !posterSrc;
  useEffect(() => {
    if (!blurFromVideo) return;
    const v = videoRef.current;
    const cv = blurCanvasRef.current;
    if (!v || !cv) return;
    const g = cv.getContext("2d");
    if (!g) return;
    const paintBlur = () => {
      if (!v.videoWidth || !v.videoHeight) return;
      const h = Math.max(1, Math.round((BLUR_CANVAS_W * v.videoHeight) / v.videoWidth));
      if (cv.width !== BLUR_CANVAS_W || cv.height !== h) {
        cv.width = BLUR_CANVAS_W;
        cv.height = h;
      }
      try {
        g.drawImage(v, 0, 0, cv.width, cv.height);
      } catch {
        /* not drawable yet */
      }
    };
    const iv = setInterval(() => {
      if (!v.paused) paintBlur();
    }, BLUR_REPAINT_MS);
    const ev = ["loadeddata", "seeked", "pause", "play"];
    ev.forEach((n) => v.addEventListener(n, paintBlur));
    paintBlur();
    return () => {
      clearInterval(iv);
      ev.forEach((n) => v.removeEventListener(n, paintBlur));
    };
  }, [blurFromVideo, source]);

  return (
    <div
      className="tplayer"
      data-testid="testimonial-player"
      data-source={source.kind}
      data-video-ratio={ratio !== null ? ratio.toFixed(4) : undefined}
      data-banded={banded ? "true" : undefined}
      style={{ aspectRatio: "4 / 3", maxHeight, maxWidth: `calc(${maxHeight} * 4 / 3)` }}
    >
      <TestimonialPlayerStyles />
      <div
        ref={rootRef}
        className="tplayer__frame"
        data-autohide={controlsHidden ? "true" : undefined}
        data-controls-shown={controlsHidden ? undefined : "true"}
        data-fs={isFs ? "true" : undefined}
        data-started={started ? "true" : undefined}
        onPointerMove={(e) => {
          if (e.pointerType === "mouse") reveal();
        }}
        onKeyDown={(e) => {
          if (e.key !== "Escape" || !menuOpen) return;
          e.stopPropagation();
          if (menu === "captions" || menu === "quality") {
            menuFocusRef.current = menu;
            setMenu("root");
          } else {
            menuFocusRef.current = "more";
            setMenu(null);
          }
        }}
      >
        {banded &&
          (posterSrc ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img className="tplayer__blur" src={posterSrc} alt="" aria-hidden="true" decoding="async" data-testid="tplayer-blur" />
          ) : (
            <canvas ref={blurCanvasRef} className="tplayer__blur" aria-hidden="true" data-testid="tplayer-blur" />
          ))}
        <video
          ref={videoRef}
          className="tplayer__video"
          playsInline
          poster={posterSrc}
          crossOrigin={source.kind === "bunny" ? "anonymous" : undefined}
          aria-label={title}
          disableRemotePlayback
          disablePictureInPicture
        />

        <div className="tplayer__tap" onClick={onTap} aria-hidden="true" />
        {overlay}

        {!started && !failed && bigPlay === "kit" && (
          <PfPlayButton className="tplayer__kit-play" onClick={onTap} label={playLabel || `Play: ${title}`} />
        )}
        {!started && !failed && bigPlay !== "kit" && (
          <button type="button" className="tplayer__big-play" onClick={onTap} aria-label={playLabel || `Play: ${title}`}>
            <PlayIcon size={32} />
          </button>
        )}
        {failed && (
          <p className="tplayer__error" role="status">
            This video could not be played here.
          </p>
        )}

        <div ref={spinnerRef} className="lps-buffering-overlay" aria-hidden="true" style={{ display: "none" }}>
          <div className="lv-equaliser">
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>

        {cueText && (
          <div className="tplayer-caption-overlay" aria-live="polite" aria-atomic="true" role="status">
            <span className="tplayer-caption-overlay__text">{cueText}</span>
          </div>
        )}

        <div
          ref={barRef}
          className="tplayer-control-bar"
          role="region"
          aria-label="Playback controls"
          data-autohide={controlsHidden ? "true" : undefined}
          onFocus={(e) => {
            let kbd = false;
            try {
              kbd = (e.target as HTMLElement).matches(":focus-visible");
            } catch {
              /* old engine */
            }
            setKbdFocusIn(kbd);
          }}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setKbdFocusIn(false);
              reveal();
            }
          }}
        >
          <div className="tplayer-scrubber-row">
            <div
              ref={scrubberRef}
              className="tplayer-scrubber"
              role="slider"
              tabIndex={0}
              aria-label="Video scrubber"
              aria-valuemin={0}
              aria-valuemax={Math.floor(shownDuration)}
              aria-valuenow={Math.floor(shownTime)}
              aria-valuetext={`${fmtTime(shownTime)} of ${fmtTime(shownDuration)}`}
              onPointerDown={onScrubDown}
              onPointerMove={onScrubMove}
              onPointerUp={onScrubUp}
              onPointerCancel={onScrubCancel}
              onLostPointerCapture={() => {
                if (scrubState.current) onScrubCancel();
              }}
              onKeyDown={onScrubKey}
            >
              <div ref={fillRef} className="tplayer-scrubber__fill" style={{ width: `${pct}%` }} />
              <div
                ref={thumbRef}
                className={`tplayer-scrubber__thumb${scrub !== null ? " tplayer-scrubber__thumb--active" : ""}`}
                style={{ left: `${pct}%` }}
                aria-hidden="true"
              />
            </div>
          </div>

          <div className="tplayer-controls-row">
            <div className="tplayer-controls-left">
              <button
                type="button"
                className="tplayer-ctrl-btn tplayer-ctrl-btn--play"
                onClick={() => {
                  togglePlay();
                  reveal();
                }}
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? <PauseIcon size={26} /> : <PlayIcon size={26} />}
              </button>
              <div className="tplayer-ctrl-divider tplayer-ctrl-divider--volume" aria-hidden="true" />
              <button type="button" className="tplayer-ctrl-btn tplayer-volume-btn" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
                {muted || volume === 0 ? <VolumeXIcon size={24} /> : <VolumeIcon size={24} />}
              </button>
              <input
                type="range"
                className="tplayer-volume-slider"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={(e) => {
                  const v = videoRef.current;
                  if (!v) return;
                  const val = parseFloat(e.target.value);
                  v.volume = val;
                  v.muted = val === 0;
                }}
                aria-label="Volume"
              />
              <span className="tplayer-ctrl-time" aria-live="off">
                <span ref={timeCurRef} className="tplayer-t-cur" />
                <span ref={timeDurRef} className="tplayer-t-dur" />
              </span>
            </div>

            <div className="tplayer-controls-right">
              {hasCaptions && (
                <button
                  type="button"
                  className={`tplayer-ctrl-btn${captionsOn ? " tplayer-ctrl-btn--cc-active" : ""}`}
                  onClick={() => setCaptionsOn((on) => !on)}
                  aria-pressed={captionsOn}
                  aria-label="Turn captions on or off"
                >
                  <CCIcon size={24} />
                </button>
              )}
              <button
                type="button"
                className={`tplayer-ctrl-btn${menuOpen ? " tplayer-ctrl-btn--settings-active" : ""}`}
                ref={moreBtnRef}
                onClick={(e) => {
                  if (e.detail === 0) menuFocusRef.current = menu ? "more" : "first";
                  setMenu((m) => (m ? null : "root"));
                }}
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                aria-label="Player settings"
              >
                <MoreIcon size={24} />
              </button>
              <button
                type="button"
                className="tplayer-ctrl-btn"
                onClick={toggleFullscreen}
                aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFs ? <CollapseIcon size={24} /> : <ExpandIcon size={24} />}
              </button>
            </div>
          </div>
        </div>

        {menuOpen && (
          <>
            <div className="tplayer-popup-backdrop" onClick={() => setMenu(null)} aria-hidden="true" />
            <div
              ref={menuRef}
              className="tplayer-settings-popup"
              role="dialog"
              aria-label="Player settings"
              onKeyDown={(e) => {
                if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
                const items = [...e.currentTarget.querySelectorAll<HTMLElement>("button")];
                if (!items.length) return;
                e.preventDefault();
                const i = items.indexOf(document.activeElement as HTMLElement);
                const n = e.key === "ArrowDown" ? i + 1 : (i < 0 ? items.length : i) - 1;
                items[(n + items.length) % items.length].focus();
              }}
              style={{
                bottom: menuPos ? menuPos.bottom : undefined,
                maxHeight: menuPos ? menuPos.maxHeight : undefined,
                visibility: menuPos ? "visible" : "hidden",
              }}
            >
              {menu === "root" && (
                <>
                  {hasCaptions && (
                    <button
                      type="button"
                      className="tplayer-settings-nav-row"
                      data-tplayer-row="captions"
                      onClick={(e) => {
                        if (e.detail === 0) menuFocusRef.current = "first";
                        setMenu("captions");
                      }}
                    >
                      <span className="tplayer-settings-nav-row__label">
                        <CCIcon size={14} /> Captions
                      </span>
                      <span className="tplayer-settings-nav-row__value">{captionsOn ? activeLabel ?? "On" : "Off"}</span>
                    </button>
                  )}
                  {renditions.length > 0 ? (
                    <button
                      type="button"
                      className="tplayer-settings-nav-row"
                      data-tplayer-row="quality"
                      onClick={(e) => {
                        if (e.detail === 0) menuFocusRef.current = "first";
                        setMenu("quality");
                      }}
                    >
                      <span className="tplayer-settings-nav-row__label">Quality</span>
                      <span className="tplayer-settings-nav-row__value">
                        {lockedHeight == null ? `Auto${activeHeight ? ` (${activeHeight}p)` : ""}` : `${lockedHeight}p`}
                      </span>
                    </button>
                  ) : (
                    !hasCaptions && (
                      <span className="tplayer-settings-nav-row tplayer-settings-nav-row--static">
                        <span className="tplayer-settings-nav-row__label">Quality</span>
                        <span className="tplayer-settings-nav-row__value">{source.kind === "file" ? "Original" : "Auto"}</span>
                      </span>
                    )
                  )}
                </>
              )}
              {menu === "captions" && (
                <>
                  <button
                    type="button"
                    className="tplayer-settings-back"
                    onClick={(e) => {
                      if (e.detail === 0) menuFocusRef.current = "captions";
                      setMenu("root");
                    }}
                    aria-label="Back to settings"
                  >
                    <ChevronLeftIcon size={14} /> Captions
                  </button>
                  {tracks.map((t) => (
                    <button
                      key={t.locale}
                      type="button"
                      className="tplayer-settings-option"
                      onClick={() => {
                        setLocale(t.locale);
                        setCaptionsOn(true);
                      }}
                    >
                      <span>
                        <CCIcon size={14} /> {t.label}
                      </span>
                      {captionsOn && activeTrack?.locale === t.locale && <CheckIcon size={14} />}
                    </button>
                  ))}
                  <button type="button" className="tplayer-settings-option" onClick={() => setCaptionsOn(false)}>
                    <span>Off</span>
                    {!captionsOn && <CheckIcon size={14} />}
                  </button>
                </>
              )}
              {menu === "quality" && (
                <>
                  <button
                    type="button"
                    className="tplayer-settings-back"
                    onClick={(e) => {
                      if (e.detail === 0) menuFocusRef.current = "quality";
                      setMenu("root");
                    }}
                    aria-label="Back to settings"
                  >
                    <ChevronLeftIcon size={14} /> Quality
                  </button>
                  <button type="button" className="tplayer-settings-option" onClick={(e) => applyQuality(-1, e.detail === 0)}>
                    <span>
                      Auto — adjusts to your internet speed
                      {activeHeight ? ` (${activeHeight}p)` : ""}
                    </span>
                    {lockedHeight == null && <CheckIcon size={14} />}
                  </button>
                  {renditions.map((l) => (
                    <button key={l.index} type="button" className="tplayer-settings-option" onClick={(e) => applyQuality(l.index, e.detail === 0)}>
                      <span>{l.height}p</span>
                      {lockedHeight === l.height && <CheckIcon size={14} />}
                    </button>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
